import React from 'react';
import styled, { css } from 'styled-components';
import { Text } from '../../Typography/Text/Text';
import ReactDOMServer from 'react-dom/server';
import { Check } from 'react-feather';
import { Size } from '../../common/size';
import { useTestId } from '../../hooks/useTestId';
import { IThemePalette } from '../../Themes/interfaces';

export const checkboxColours = (palette: IThemePalette) => ({
  checkbox: {
    border: palette.primary.text[25],
    focus: {
      background: palette.primary.first[75]
    },
    selected: {
      background: palette.primary.first[100],
      text: palette.neutral.white
    },
    invalid: {
      text: palette.alerts.alert,
      border: palette.alerts.alert
    }
  }
});

const CheckBoxContainer = styled.label<ICheckBoxProps>`
  display: flex;
  cursor: pointer;
  flex-direction: row;
  position: relative;
  ${Text} {
    padding: 0 1rem;
    text-align: start;
    margin-left: 0;
  }
  > input {
    width: 2rem;
    min-width: 2rem;
    height: 2rem;
    appearance: none;
    border: 1px solid ${({ theme }) => theme.colours.checkbox.border};
    box-sizing: border-box;
    border-radius: 0.25rem;
    outline: none;
    cursor: pointer;
    background-repeat: no-repeat;
    background-position: center;
    background-origin: content-box;
    vertical-align: middle;
    margin: 0;
    ${({ invalid }) =>
      invalid &&
      css`
        border: 1px solid ${({ theme }) => theme.colours.checkbox.invalid.border};
        color: ${({ theme }) => theme.colours.checkbox.invalid.text};
      `}
  }
  > input:checked {
    background-image: ${({ theme }) =>
      `url(data:image/svg+xml;base64,${btoa(ReactDOMServer.renderToStaticMarkup(<Check color={theme.colours.checkbox.selected.text} />))})`};
    background-color: ${({ theme }) => theme.colours.checkbox.selected.background};
    color: ${({ theme }) => theme.colours.checkbox.selected.text};
    border: none;
  }
  > input:hover,
  input:focus {
    background-color: ${({ theme }) => theme.colours.checkbox.focus.background};
  }
`;

export interface ICheckBoxProps extends React.InputHTMLAttributes<HTMLInputElement> {
  label?: string;
  invalid?: boolean;
}

export const CheckBox = ({ label, invalid, ...props }: ICheckBoxProps) => {
  const { generateTestId } = useTestId(props, 'CheckBox');
  return (
    <CheckBoxContainer invalid={invalid} {...generateTestId()}>
      <input type='checkbox' {...props} {...generateTestId('input')} />
      {label && (
        <Text size={Size.Medium} {...generateTestId('Text')}>
          {label}
        </Text>
      )}
    </CheckBoxContainer>
  );
};
